import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { Brand, BrandsApi } from '../api/brands'

export function BrandSettings({ api }: { api: BrandsApi }) {
  const brands = useQuery({ queryKey: ['brands'], queryFn: () => api.list() })
  if (brands.isPending) return <p>Reading brands...</p>
  if (brands.isError) return <p role="alert">{brands.error.message} Only the Owner may configure brands.</p>
  return <div className="space-y-6">{brands.data.map(brand => <BrandForm key={brand.code} brand={brand} api={api} />)}</div>
}

function BrandForm({ brand, api }: { brand: Brand; api: BrandsApi }) {
  const client = useQueryClient()
  const [name, setName] = useState(brand.name)
  const [origin, setOrigin] = useState(brand.marketingOrigin ?? '')
  const [email, setEmail] = useState(brand.supportEmail ?? '')
  const save = useMutation({
    mutationFn: () => api.save(brand.code, { name, marketingOrigin: origin.trim() || null, supportEmail: email.trim() || null }),
    onSuccess: saved => {
      setName(saved.name); setOrigin(saved.marketingOrigin ?? ''); setEmail(saved.supportEmail ?? '')
      client.setQueryData<Brand[]>(['brands'], held => held?.map(item => item.code === saved.code ? saved : item))
    },
  })
  return <form aria-label={brand.name} className="max-w-xl rounded-lg border border-ink-100 bg-card p-6"
    onSubmit={event => { event.preventDefault(); save.mutate() }}>
    <h3 className="font-bold">{brand.code}</h3>
    <label className="mt-4 block">Brand name
      <input className="mt-1 block w-full rounded-md border border-ink-200 bg-page px-3 py-2" required maxLength={128} value={name} onChange={event => setName(event.target.value)} />
    </label>
    <label className="mt-4 block">Marketing origin
      <input className="mt-1 block w-full rounded-md border border-ink-200 bg-page px-3 py-2" type="url" placeholder="https://example.com" value={origin} onChange={event => setOrigin(event.target.value)} />
    </label>
    <p className="mt-1 text-sm text-muted">Leave blank until the site is available. This setting does not create or publish a website.</p>
    <label className="mt-4 block">Support email
      <input className="mt-1 block w-full rounded-md border border-ink-200 bg-page px-3 py-2" type="email" value={email} onChange={event => setEmail(event.target.value)} />
    </label>
    <button type="submit" className="mt-4 rounded-md border border-green-800 px-4 py-2 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55" disabled={save.isPending}>{save.isPending ? 'Saving...' : 'Save'}</button>
    {save.isSuccess && <p role="status">Saved</p>}
    {save.isError && <p role="alert">{save.error.message}</p>}
  </form>
}
